/**
 * What the "Something went wrong" screens do with an error:
 *
 *  - report it to the API log (`POST /api/t/error` → "Browser error on <page>: …"),
 *    so a crash a customer saw can be found and fixed;
 *  - recover by themselves from the two temporary kinds, once per page:
 *      "chunk": the browser holds an old copy of the app after a new release
 *               (or a dev-server rebuild) and a piece of code is gone → reload.
 *               Also: a missing export between two builds ("(0 , x.y) is not a
 *               function") and any crash after an app file failed to load;
 *      "dom":   a browser extension or the browser's own translation changed
 *               the page under React (removeChild / insertBefore errors) →
 *               render the page again.
 *
 * Anything else is shown as usual, with its details available to copy.
 */
import { apiAuthHeaders, apiBaseUrl } from "./api";

export type ErrorKind = "chunk" | "dom" | "other";

const CHUNK =
  /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to load chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const DOM =
  /removeChild|insertBefore|not a child of this node|The node to be removed is not a child|NotFoundError/i;

// A missing export between two builds: "(0 , g.groupFeatures) is not a function",
// or webpack's "Cannot read properties of undefined (reading 'call')".
// Turbopack's version of the same thing: "Module 71645 was instantiated because it was required from module
// 75254, but the module factory is not available" (a page's old code asks for a piece the new release removed).
const SKEW = /\(0\s*,\s*[\w$]+\.[\w$]+\) is not a function|reading 'call'\)|__webpack_require__|is not a function \(it is undefined\)|module factory is not available|was instantiated because it was required from module/i;

/** Set by the inline script in app/layout.tsx when an app file (/_next/static/…) failed to load. */
export function staleAssetsSeen(withinMs = 2 * 60_000): boolean {
  if (typeof window === "undefined") return false;
  const at = (window as unknown as { __rbStale?: number }).__rbStale;
  return typeof at === "number" && Date.now() - at < withinMs;
}

export function classify(error: Error): ErrorKind {
  const text = `${error?.name ?? ""} ${error?.message ?? ""}`;
  if (CHUNK.test(text) || SKEW.test(text)) return "chunk";
  if (DOM.test(text)) return "dom";
  // Any crash right after app files failed to load is an out-of-date copy of the app.
  if (staleAssetsSeen()) return "chunk";
  return "other";
}

const RECOVER_KEY = "rb_err_recover";
const WINDOW_MS = 30_000;

/** True when this page may still recover by itself (once per 30 s per page and kind). */
function mayRecover(kind: ErrorKind): boolean {
  if (kind === "other") return false;
  const id = `${kind}:${window.location.pathname}`;
  try {
    const raw = window.sessionStorage.getItem(RECOVER_KEY);
    const last = raw ? (JSON.parse(raw) as { id?: string; at?: number }) : null;
    if (last?.id === id && typeof last.at === "number" && Date.now() - last.at < WINDOW_MS) return false;
    window.sessionStorage.setItem(RECOVER_KEY, JSON.stringify({ id, at: Date.now() }));
    return true;
  } catch {
    // storage blocked: allow one try for this page load only
    const w = window as unknown as { __rbRecovered?: string };
    if (w.__rbRecovered === id) return false;
    w.__rbRecovered = id;
    return true;
  }
}

/** Send the error to the API log. Never throws. */
export function reportError(error: Error & { digest?: string }, boundary: string, recovered: boolean): void {
  try {
    const body = JSON.stringify({
      page: window.location.pathname + window.location.search,
      name: error?.name,
      message: error?.message,
      digest: error?.digest,
      stack: (error?.stack ?? "").slice(0, 4000),
      boundary,
      recovered,
    });
    fetch(`${apiBaseUrl}/api/t/error`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...apiAuthHeaders() },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* reporting must never cause a second error */
  }
}

const handled = new WeakMap<object, boolean>();

/**
 * Report the error and, for a temporary kind, start recovering. Returns true
 * while recovering, so the screen can say "Reloading…" instead of an error.
 */
export function handleBoundaryError(error: Error & { digest?: string }, boundary: string, reset: () => void): boolean {
  // React may run the screen's effect twice for one error (development); act once.
  if (error && typeof error === "object" && handled.has(error)) return handled.get(error)!;
  const kind = classify(error);
  const recover = mayRecover(kind);
  reportError(error, boundary, recover);
  if (error && typeof error === "object") handled.set(error, recover);
  if (!recover) return false;
  if (kind === "chunk") window.location.reload();
  else setTimeout(reset, 0);
  return true;
}

/** Plain-text details for the "Copy details" button. */
export function errorDetails(error: Error & { digest?: string }): string {
  return [
    `Page: ${typeof window !== "undefined" ? window.location.href : ""}`,
    `Time: ${new Date().toISOString()}`,
    `Error: ${error?.name ?? "Error"}: ${error?.message ?? ""}`,
    error?.digest ? `Digest: ${error.digest}` : "",
    `Browser: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
    "",
    (error?.stack ?? "").slice(0, 3000),
  ]
    .filter((l, i) => l || i > 4)
    .join("\n");
}
